import React, { useState, useEffect } from "react";
import { Box, Grid, Paper, List, ListItem, ListItemText, Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import Toolbar from '@mui/material/Toolbar';
import Card from "@mui/material/Card";
import { useNavigate, useParams } from 'react-router-dom';

import { ThemeProvider } from "@mui/material/styles";
import setThemeObj from '../hooks/useThemeMUI';
import useMediaQuery from '@mui/material/useMediaQuery';

import { Calendar } from 'primereact/calendar';
//import "primereact/resources/themes/md-light-indigo/theme.css";

import { gql } from '@apollo/client';
import apolloClient from '../apolloClient';
import * as types from "../gql/graphql";

import MDTypographyRoot from './MDTypography/MDTypographyRoot';
import MDBoxRoot from './MDBox/MDBoxRoot';
import MDButtonRoot from "./MDButton/MDButtonRoot";
import { CalendarTree } from "../utils/CalendarUtils"
import { SchoolTime } from "../utils/SchoolTime";

// Max number of months to show in calendar
const MaxNumberOfMonths = 3;

function getMonths(month: SchoolTime): SchoolTime[] {
  var tm = SchoolTime.fromSchoolTime(month);
  var monthsArray = [];
  // Loop to get four months
  for (var i = 0; i < MaxNumberOfMonths; i++) {
    monthsArray.push(tm);
    tm = tm.addMonths(1);
  }
  return monthsArray;
}

export default function EventCalendar() {
  const getEventsQuery = gql(`
  query GetEvents($filter:fs_eventsFilter, $after: Cursor) {
    fs_eventsCollection(filter: $filter, after: $after) {
        pageInfo { endCursor startCursor hasNextPage }
        edges {
          node {
            nodeId
            id
            class
            fs_classes { name }
            capacity
            date
            status         
          }
        }
      }
  }`);

  var monthStart = SchoolTime.now().setMonthStart();
  //var first = monthStart.addMonths(-1);
  var first = monthStart;

  const [firstMonth, setFirstMonth] = useState<SchoolTime>(first);
  //const [selectedDay, setSelectedDay] = useState(null);
  const { day } = useParams();
  const selectedDay = day ? SchoolTime.fromISO(day + 'T00:00:00') : null;
  // TODO: check that dates contain the day, could be not the case if navigated to a bookmark
  const monthsArray = getMonths(firstMonth);
  const [dates, setDates] = useState<CalendarTree<types.Fs_Events>>(null);
  const [selectedEvent, setSelectedEvent] = useState<types.Fs_Events>(null);

  const navigate = useNavigate();

  function onEventUpdate(uev: types.Fs_Events): void {
    if (!uev) return;
    if (selectedEvent.id > -1) dates.deleteEvent(selectedEvent);
    dates.addEvent(uev);
    setSelectedEvent(null);
  }

  function onEventDelete(uev: types.Fs_Events): void {
    if (!uev) return;
    dates.deleteEvent(uev);
    setSelectedEvent(null);
  }

  useEffect(() => {
    // Fetch all pages for each month
    const fetchAllPagesForMonth = async (m: SchoolTime, nm: SchoolTime) => {
      let hasNextPage = true;
      let cursor: string | null = null;
      let allEvents: any[] = [];

      while (hasNextPage) {
        try {
          const { data } = await apolloClient.query({
            query: getEventsQuery,
            variables: {
              filter: {
                and: [
                  { date: { gte: m.toISOLocal() } },
                  { date: { lt: nm.toISOLocal() } },
                ],
              },
              after: cursor,
            },
            fetchPolicy: 'network-only',
          });

          // Collect all events
          allEvents.push(...data?.fs_eventsCollection.edges.map((e) => e.node));

          // Check for more pages
          hasNextPage = data?.fs_eventsCollection.pageInfo.hasNextPage;
          cursor = data?.fs_eventsCollection.pageInfo.endCursor;

        } catch (error) {
          console.error('Error fetching events for month:', m, error);
          hasNextPage = false; // Stop on error
        }
      }

      return allEvents;
    };

    const fetchEventsByMonth = async () => {
      try {
        const promises = monthsArray
          .filter((m) => !dates?.hasMonth(m)) // Only fetch missing months
          .map((m) => {
            const nm = m.setMonth(m.getMonth() + 1);
            return fetchAllPagesForMonth(m, nm);
          });

        if (promises.length === 0) return; // No new months to fetch

        // Wait for all event arrays to resolve
        const eventArrays = await Promise.all(promises);

        // Flatten the event arrays into a single array
        const allEvents = eventArrays.flat();

        // Create a new CalendarTree and load all events
        const updatedTree = new CalendarTree<types.Fs_Events>(dates);
        allEvents.forEach((event) => updatedTree.addEvent(event));

        setDates(updatedTree);

      } catch (error) {
        console.error('Error fetching events:', error);
      }
    };

    fetchEventsByMonth();
  }, [firstMonth]);


  const dateTemplate = (date) => {
    const dl = dates?.getEvents(date.year, date.month + 1, date.day);
    if (dl?.length > 0) {
      return (
        <div><strong style={{ fontWeight: 'bolder' }}>{date.day}</strong> <sup className="num-circle">{dl.length}</sup></div>
      );
    }

    return date.day;
  }

  function updateMonths(e) {
    setFirstMonth(SchoolTime.fromJSDate(e.value));
  }

  const handleAddClick = () => {
    let formattedDate = selectedDay.toISOLocal().split('T')[0]; // 'YYYY-MM-DD' format
    navigate("../calendar/" + formattedDate + "/new");

  }

  const handleDateClick = (e) => {
    let formattedDate = SchoolTime.fromJSDate(e.value).toDateTime().toISODate(); // 'YYYY-MM-DD' format
    navigate("../calendar/" + formattedDate);
  };

  const isSmallScreen = useMediaQuery(setThemeObj.breakpoints.down('sm'));
  const isUnderMediumScreen = useMediaQuery(setThemeObj.breakpoints.between('sm', 'md'));
  const isMediumScreen = useMediaQuery(setThemeObj.breakpoints.up('md'));
  const [numberOfMonths, setNumberOfMonths] = useState(3);

  useEffect(() => {
    if (isSmallScreen) {
      setNumberOfMonths(1);
    } else if (isUnderMediumScreen) {
      setNumberOfMonths(2);
    } else {
      setNumberOfMonths(3);
    }
  }, [isSmallScreen, isUnderMediumScreen]);

  function DayList() {
    const handleEventClick = (ev) => {
      let formattedDate = selectedDay.toISOLocal().split('T')[0]; // 'YYYY-MM-DD' format
      navigate("../calendar/" + formattedDate + "/" + ev.id, { state: { event: ev, dates: dates } });
    };

    return <List>
      {
        selectedDay && dates?.getEvents(selectedDay.getYear(), selectedDay.getMonth(), selectedDay.getDay())?.
          map((item, index) => (
            <ListItem key={item.id}>
              <ListItemText>
                <MDButtonRoot
                  ownerState={{
                    size: "large",
                    variant: "text",
                    color: "secondary",
                    textTransform: "none",
                  }}
                  onClick={() => handleEventClick(item)}>
                  <MDTypographyRoot variant="h4" px={2} ownerState={{ color: "dark" }}> {(SchoolTime.fromISO(item.date)).toDateTime().toFormat("t")}</MDTypographyRoot>
                  {item.fs_classes.name}
                </MDButtonRoot>
              </ListItemText>
            </ListItem>
          ))}
    </List>
  }

  return (
    <ThemeProvider theme={setThemeObj}>
      <Card>
        <MDBoxRoot
          component="main" px={isSmallScreen ? 2 : 4} py={4}
          sx={{ flexGrow: 1, width: '100%' }}
        >
          <Toolbar />
          <Card sx={{ marginTop: '10px' }}>
            <Calendar
              viewDate={firstMonth.toJSDate()}
              inline onChange={handleDateClick}
              dateTemplate={dateTemplate} numberOfMonths={numberOfMonths}
              onViewDateChange={updateMonths}
              style={{ borderLeft: "1px solid rgba(0, 0, 0, 0.12)" }} />
          </Card>
          <Card sx={{ marginTop: "10px", padding: "4px 16px 8px" }}>
            <Grid item xs={11}>
              <MDTypographyRoot
                variant="h4"
                ownerState={{ color: "secondary" }}>
                {selectedDay?.toDateTime().toFormat('DDDD')}
              </MDTypographyRoot>
              {selectedDay && (
                <MDBoxRoot px={0} py={1}>
                  <MDButtonRoot
                    ownerState={{
                      size: "medium",
                      variant: "text",
                      color: "info",
                      textTransform: "uppercase",
                    }}
                    onClick={handleAddClick} startIcon={<AddIcon />}
                  > Add event
                  </MDButtonRoot>
                </MDBoxRoot>
              )}
              <MDBoxRoot component="table" width="100%">
                <MDBoxRoot display="flex"
                  justifyContent="space-between"
                  flexDirection={isSmallScreen ? 'column' : 'row'}
                >
                  <MDBoxRoot style={{ verticalAlign: "top" }}>
                    <DayList />
                  </MDBoxRoot>
                </MDBoxRoot>
              </MDBoxRoot>
            </Grid>
          </Card>
        </MDBoxRoot>
      </Card>
    </ThemeProvider>
  )
}



